// Section ordering for the SHSAT "start with ELA or Math first" feature.
//
// Both the legacy (positional modules[0]/modules[1]) and adaptive (index-based
// advance) flows assume the `modules` array is in FLOW order — i.e. the section
// the student does first is index 0. So a Math-first choice must physically
// reorder the array, and on resume we must reconstruct that same flow order.
//
// We do NOT persist the choice (no DB field). Instead:
//   - Fresh start: order by the student's explicit choice (orderByChoice).
//   - Resume:      order by progress (orderByProgress) — sections the student
//                  has already touched come first, which reconstructs the chosen
//                  order for a 2-section (ELA + Math) test without persistence.

export type StartSection = 'ela' | 'math';

// Display label for a section subject in the SHSAT top bars / start screens.
// "English Language Arts" -> "ELA" and "Mathematics" -> "Math"; other subjects
// are returned unchanged. Case-insensitive; tolerates section names that embed
// the test name (e.g. adaptive's "Digital SHSAT Practice Test A1 Mathematics").
export function sectionDisplayLabel(subject?: string | null): string {
    const s = (subject ?? '').trim();
    if (/english|\bela\b/i.test(s)) return 'ELA';
    if (/math/i.test(s)) return 'Math';
    return s;
}

// Whether a subject/section is ELA (case-insensitive). The ELA section is the
// one that ends on the "Stand alone items" (Revising/Editing Part B) block.
export function isElaSection(subject?: string | null): boolean {
    return /english|\bela\b/i.test((subject ?? '').trim());
}

// Label for the ELA unified "Stand alone" block (Revising/Editing Part A +
// Part B) — the START screen ("ELA – Stand alone items") and, upper-cased, the
// top-bar label ("STAND ALONE ITEMS"). Has no data field — a frontend constant,
// same pattern as the directions wording in SECTION_DIRECTIONS_PROMPTS.
export const STANDALONE_ITEMS_LABEL = 'Stand alone items';

// Title for the end-of-section screen: "End of ELA – Stand alone items" (when
// the section ends on the stand-alone block) or "End of Math". The section
// name drives the abbreviation (data-driven); the sub-label is the constant
// above, applied only when ``endsOnStandalone``.
export function sectionEndTitle(
    subject: string | null | undefined,
    endsOnStandalone: boolean,
): string {
    const base = `End of ${sectionDisplayLabel(subject)}`;
    return endsOnStandalone ? `${base} – ${STANDALONE_ITEMS_LABEL}` : base;
}

const isMathSection = (m: { subject?: string; sectionName?: string }): boolean =>
    (m.subject || m.sectionName || '').toLowerCase().includes('math');

// Fresh-start ordering by explicit choice. 'math' moves the Math section(s)
// ahead of the rest; 'ela' keeps the backend order (ELA already first).
// Relative order within each group is preserved (stable).
export function orderByChoice<T extends { subject?: string; sectionName?: string }>(
    modules: T[],
    startSection: StartSection,
): T[] {
    if (startSection !== 'math') return modules;
    return [...modules.filter(isMathSection), ...modules.filter((m) => !isMathSection(m))];
}

// Resume ordering by progress. Sections the student has already touched
// (`isTouched` true) come first, preserving their relative order, then the
// untouched ones. For a linear 2-section test this recovers the flow order the
// student originally chose, so index/positional logic keeps working.
//
// Callers typically stack two passes — touched-first, then complete-first —
// so a student who FINISHED Math and then started ELA resumes with Math at
// index 0 (the true flow order) rather than the backend's ELA-first order:
//     orderByProgress(orderByProgress(modules, isTouched), isComplete)
export function orderByProgress<T>(modules: T[], isTouched: (m: T) => boolean): T[] {
    const touched = modules.filter(isTouched);
    const untouched = modules.filter((m) => !isTouched(m));
    return [...touched, ...untouched];
}

// Whether the "start with ELA or Math first" picker applies: the test must
// actually contain both a Math section and a non-Math (ELA) section.
// Single-subject assignments have nothing to reorder.
export function hasSectionChoice<T extends { subject?: string; sectionName?: string }>(
    modules: T[],
): boolean {
    return modules.some(isMathSection) && modules.some((m) => !isMathSection(m));
}

// ---------------------------------------------------------------------------
// Lightweight frontend-only persistence of the choice (NO DB field).
//
// Written when the student confirms Start (legacy beginTest / adaptive
// onStart) so a refresh or reconnect BEFORE any progress exists still
// reconstructs the chosen flow order. Keyed per test, mirroring the
// existing localStorage patterns ('shsat_position', the adaptive timer
// key). Cleared on submit/discard so a fresh retake starts clean.
// Readers must treat it as a hint: when absent (older sessions), fall
// back to deriving the order from progress (orderByProgress).
// ---------------------------------------------------------------------------

function startSectionStorageKey(testId: string): string {
    return `shsat_start_section_${testId}`;
}

export function saveStartSection(testId: string, section: StartSection): void {
    try {
        localStorage.setItem(startSectionStorageKey(testId), section);
    } catch {
        // private browsing / quota — the progress-based fallback still works
    }
}

export function readStartSection(testId: string): StartSection | null {
    try {
        const stored = localStorage.getItem(startSectionStorageKey(testId));
        return stored === 'ela' || stored === 'math' ? stored : null;
    } catch {
        return null;
    }
}

export function clearStartSection(testId: string): void {
    try {
        localStorage.removeItem(startSectionStorageKey(testId));
    } catch {
        // ignore
    }
}
